import React, { useMemo, useState } from 'react';
import type { LscArchiveItem } from '../../types';
import { countUnavailableArchivePlayback } from '../../archivePlayback';
import { ARCHIVE_RETENTION_LIBRARY_HELP_COPY } from '../../archiveRetentionCopy';
import PaginatedListFooter from '../PaginatedListFooter';
import EmptyState from '../EmptyState';
import { useLeague } from '../../hooks/useLeague';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import ArchiveLibraryCard from './ArchiveLibraryCard';
import {
  buildArchiveLibraryFilterOptions,
  EMPTY_ARCHIVE_LIBRARY_FILTERS,
  filterArchiveLibraryItems,
  groupArchivesBySeasonAndStage,
  hasActiveArchiveLibraryFilters,
  type ArchiveLibraryFilters,
} from './archiveLibraryUtils';

export type LeagueArchivesLibraryProps = {
  leagueId: string;
  items: LscArchiveItem[];
  isCommissioner?: boolean;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  matchFilter?: string;
};

const LeagueArchivesLibrary: React.FC<LeagueArchivesLibraryProps> = ({
  leagueId,
  items,
  isCommissioner = false,
  statusFilter,
  onStatusFilterChange,
  matchFilter = '',
}) => {
  const { league } = useLeague();
  const activeEdition = league?.active_edition;
  const [filters, setFilters] = useState<ArchiveLibraryFilters>(
    EMPTY_ARCHIVE_LIBRARY_FILTERS
  );

  const filterOptions = useMemo(
    () => buildArchiveLibraryFilterOptions(items, activeEdition),
    [items, activeEdition]
  );

  const filteredItems = useMemo(
    () => filterArchiveLibraryItems(items, filters, activeEdition),
    [items, filters, activeEdition]
  );

  const unavailablePlaybackCount = useMemo(
    () => countUnavailableArchivePlayback(items),
    [items]
  );

  const pagination = usePaginatedList(filteredItems);

  const groupedItems = useMemo(
    () => groupArchivesBySeasonAndStage(pagination.visibleItems, activeEdition),
    [pagination.visibleItems, activeEdition]
  );

  const clearFilters = () => setFilters(EMPTY_ARCHIVE_LIBRARY_FILTERS);

  return (
    <div className="space-y-6 min-w-0 overflow-x-hidden" data-testid="league-archives-library">
      <p
        className="rounded-lg border border-secondary-700/80 bg-secondary-900/30 px-4 py-3 text-sm text-secondary-400"
        data-testid="archive-library-retention-help"
      >
        {ARCHIVE_RETENTION_LIBRARY_HELP_COPY}
      </p>

      <div
        className="max-w-lsc-narrow space-y-4 rounded-xl border border-secondary-700 bg-secondary-900/40 p-4"
        data-testid="archive-library-filters"
      >
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <label className="flex flex-col gap-1 text-sm md:col-span-2 xl:col-span-3">
            <span className="text-secondary-400">Search replays</span>
            <input
              type="search"
              className="input-field"
              placeholder="Search by matchup, team, week, season, or broadcaster"
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({ ...current, search: event.target.value }))
              }
              data-testid="archive-library-search"
            />
          </label>

          <FilterSelect
            label="Season"
            value={filters.season}
            options={filterOptions.seasons}
            onChange={(season) => setFilters((current) => ({ ...current, season }))}
            testId="archive-library-season-filter"
          />
          <FilterSelect
            label="Week / stage"
            value={filters.stage}
            options={filterOptions.stages}
            onChange={(stage) => setFilters((current) => ({ ...current, stage }))}
            testId="archive-library-stage-filter"
          />
          <FilterSelect
            label="Team"
            value={filters.team}
            options={filterOptions.teams}
            onChange={(team) => setFilters((current) => ({ ...current, team }))}
            testId="archive-library-team-filter"
          />
          <FilterSelect
            label="Broadcaster"
            value={filters.broadcaster}
            options={filterOptions.broadcasters}
            onChange={(broadcaster) =>
              setFilters((current) => ({ ...current, broadcaster }))
            }
            testId="archive-library-broadcaster-filter"
            formatOption={(value) => `@${value}`}
          />

          <FilterSelect
            label="Playback"
            value={filters.playback}
            options={['playable', 'unavailable', 'all']}
            onChange={(playback) =>
              setFilters((current) => ({
                ...current,
                playback: playback as ArchiveLibraryFilters['playback'],
              }))
            }
            testId="archive-library-playback-filter"
            formatOption={(value) => {
              if (value === 'playable') return 'Playable replays';
              if (value === 'unavailable') return 'Unavailable video';
              return 'All archived games';
            }}
          />

          {isCommissioner ? (
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-secondary-400">Status</span>
              <select
                className="input-field"
                value={statusFilter}
                onChange={(event) => onStatusFilterChange(event.target.value)}
                data-testid="archive-library-status-filter"
              >
                <option value="">Published (default)</option>
                <option value="published">Published only</option>
                <option value="unpublished">Unpublished</option>
              </select>
            </label>
          ) : null}
        </div>

        {hasActiveArchiveLibraryFilters(filters) ? (
          <button
            type="button"
            className="text-sm text-primary-300 hover:text-primary-200"
            onClick={clearFilters}
            data-testid="archive-library-clear-filters"
          >
            Clear filters
          </button>
        ) : null}

        {matchFilter ? (
          <p className="text-xs text-secondary-500">
            Filtered to match: <span className="font-mono">{matchFilter}</span>
          </p>
        ) : null}

        {unavailablePlaybackCount > 0 && filters.playback === 'playable' ? (
          <p className="text-xs text-secondary-500" data-testid="archive-library-unavailable-notice">
            {unavailablePlaybackCount} archived{' '}
            {unavailablePlaybackCount === 1 ? 'game has' : 'games have'} unavailable Twitch video.
          </p>
        ) : null}
      </div>

      {filteredItems.length === 0 ? (
        <EmptyState
          title="No replays match your filters"
          message={
            hasActiveArchiveLibraryFilters(filters)
              ? 'Try clearing filters or searching with a different matchup, week, or broadcaster.'
              : matchFilter
                ? 'No published streams for this match yet.'
                : 'No published streams yet.'
          }
        />
      ) : (
        <div className="space-y-8" data-testid="archive-library-groups">
          {groupedItems.map((seasonGroup) => (
            <section
              key={seasonGroup.seasonLabel}
              className="space-y-5"
              data-testid="archive-library-season-group"
              data-season-label={seasonGroup.seasonLabel}
            >
              <h2 className="text-lg font-semibold text-white border-b border-secondary-700 pb-2">
                {seasonGroup.seasonLabel}
              </h2>
              {seasonGroup.stages.map((stageGroup) => (
                <div
                  key={`${seasonGroup.seasonLabel}-${stageGroup.stageLabel}`}
                  className="space-y-3 rounded-xl border border-secondary-700 bg-secondary-900/30 p-4"
                  data-testid="archive-library-stage-group"
                  data-stage-label={stageGroup.stageLabel}
                >
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-secondary-300 border-b border-secondary-800 pb-2">
                    {stageGroup.stageLabel}
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {stageGroup.archives.map((archive) => (
                      <ArchiveLibraryCard
                        key={archive.id}
                        leagueId={leagueId}
                        archive={archive}
                        showStatus={isCommissioner}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ))}
        </div>
      )}
      {filteredItems.length > 0 ? (
        <PaginatedListFooter
          visibleCount={pagination.visibleCount}
          totalCount={pagination.totalCount}
          hasMore={pagination.hasMore}
          onShowMore={pagination.showMore}
          itemLabel="archives"
        />
      ) : null}
    </div>
  );
};

function FilterSelect({
  label,
  value,
  options,
  onChange,
  testId,
  formatOption,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  testId: string;
  formatOption?: (value: string) => string;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-secondary-400">{label}</span>
      <select
        className="input-field"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
      >
        <option value="">All</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {formatOption ? formatOption(option) : option}
          </option>
        ))}
      </select>
    </label>
  );
}

export default LeagueArchivesLibrary;
